import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Card, DatePicker, Flex, Typography, Modal } from 'antd'
import {
  CButton,
  CFormInput,
  CMultiSelect,
  CSmartTable,
  CSpinner,
} from '@coreui/react-pro'
import {
  $hasMoreTransactionsDetail,
  $hasMoreTransactionsInProgress,
  $loadedPageOfTransactionsDetail,
  $loadedPageOfTransactionsInProgress,
  $transactionsDetails,
  $transactionsInProgress,
  getInProgressTransactions,
  getTransactionsDetails,
  transactionsDetailsMounted,
  transactionsDetailsRefresh,
  transactionsInProgressDetailsMounted,
} from './model'
import { useStore } from 'effector-react'
import { useInfiniteScroll } from '../../../../../libs'
import { Option } from '@coreui/react-pro/src/components/multi-select/types'
import {
  inProgressTransactionTableColumns,
  TRANSACTIONS_PAGE_SIZE,
  transactionDetailsType,
  transactionTableColumns,
  WalletStatus,
} from './const'
import { rangePresets } from '../../../../billing/transactions/list/consts'
import {
  TransactionModalState,
  RangeValue,
  TransactionsDetailsItemFromServer,
} from './types'
import { $gatewaysDetails } from '../GatewaysDetails/model'
import { cilDataTransferDown } from '@coreui/icons'
import CIcon from '@coreui/icons-react'
import { SorterValue } from '@coreui/react-pro/src/components/smart-table/types'
import { OtherTransactionModal } from '../OtherTransactionModal'
import { BudgetPaymentTransactionModal } from '../BudgetPaymentTransactionModal'
import { getTransactionItems } from './utils'
import { mapScopedColumns } from './mapScopedColumns'
import { DividendTransactionModal } from '../DividendTransactionModal'
import { InvestmentTransactionModal } from '../InvestmentTransactionModal'
import { Currency } from '../../../../../views/widgets/walletCard/types'
import { TransferTransactionModal } from '../TransferTransactionModal'
import { FilterModal } from '../FilterModal'
import {
  allWalletMounted,
  walletAllowedListMounted,
} from '../GeneralTransactionModal/model'
import { CommissionPaymentTransactionModal } from '../CommissionPaymentTransactionModal'
import { EditWalletTransactionModal } from '../EditWalletTransactionModal'
import { EditWalletTransaction } from '../EditWalletTransactionModal/types'

type Props = {
  currenciesFromWallet: string[]
  allCurrencies: Currency[]
  walletStatus: WalletStatus
  // Известен только на странице конкретного кошелька (WalletStatus.general),
  // на странице "in progress" транзакции отображены по всем кошелькам
  walletId?: string
}

export const TransactionsDetails: React.FC<Props> = ({
  currenciesFromWallet,
  allCurrencies,
  walletStatus,
  walletId,
}) => {
  const [columnSorter, setColumnSorter] = useState<SorterValue>({
    column: '',
    state: '',
  })

  const [isOpenModal, setIsOpenModal] = useState<TransactionModalState>({
    createBudgetPaymentModal: false,
    createCommissionPaymentModal: false,
    createOtherModal: false,
    createTransferModal: false,
    createInvestmentModal: false,
    createDividendModal: false,
    editWalletTransactionModal: false,
    transaction: null,
    createChargebackModal: false,
    createDebtModal: false,
  })

  // Номер запрошенной страницы нужен только чтобы отличить первую загрузку
  // от подгрузки; какую страницу грузить дальше, решает модель.
  const [requestedPage, setRequestedPage] = useState(1)
  const [reloadNonce, setReloadNonce] = useState(0)
  const [searchText, setSearchText] = useState('')
  const [filtersState, setFiltersState] = useState<{
    [key: string]: string | string[] | RangeValue
  }>({})

  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false)

  const isGeneral = walletStatus === WalletStatus.general

  const tableColumns = isGeneral
    ? transactionTableColumns
    : inProgressTransactionTableColumns

  const transactionsDetails = useStore(
    isGeneral ? $transactionsDetails : $transactionsInProgress,
  )
  const transactionsLoading = useStore(
    isGeneral
      ? getTransactionsDetails.pending
      : getInProgressTransactions.pending,
  )
  const loadedPage = useStore(
    isGeneral
      ? $loadedPageOfTransactionsDetail
      : $loadedPageOfTransactionsInProgress,
  )
  const hasMore = useStore(
    isGeneral ? $hasMoreTransactionsDetail : $hasMoreTransactionsInProgress,
  )

  const gatewaysDetail = useStore($gatewaysDetails)

  useEffect(() => {
    allWalletMounted()
    walletAllowedListMounted()
  }, [])

  const buildRequest = useCallback(
    (page: number) => ({
      limit: TRANSACTIONS_PAGE_SIZE,
      page,
      orderBy: columnSorter.column as string,
      orderDirection: columnSorter.state as string,
      searchText,
      filters: filtersState,
    }),
    [columnSorter.column, columnSorter.state, searchText, filtersState],
  )

  const requestPage = useCallback(
    (page: number) => {
      setRequestedPage(page)

      if (isGeneral) {
        transactionsDetailsMounted(buildRequest(page))
      } else if (walletStatus === WalletStatus.inProgress) {
        transactionsInProgressDetailsMounted(buildRequest(page))
      }
    },
    [buildRequest, isGeneral, walletStatus],
  )

  // Смена фильтров, поиска, сортировки и явная перезагрузка — всегда с первой
  // страницы, модель на первой странице заменяет список целиком.
  useEffect(() => {
    requestPage(1)
  }, [requestPage, reloadNonce])

  const reloadTransactions = () => {
    setReloadNonce((nonce) => nonce + 1)
  }

  useEffect(() => {
    return transactionsDetailsRefresh.watch(() => {
      reloadTransactions()
    })
  }, [])

  const tableItems = useMemo(
    () => getTransactionItems(transactionsDetails),
    [transactionsDetails],
  )
  const isLoadingMore = transactionsLoading && requestedPage > 1

  const sentinelRef = useInfiniteScroll({
    hasMore,
    isLoading: transactionsLoading,
    onLoadMore: () => requestPage(loadedPage + 1),
  })

  const handleChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const name = event.currentTarget.name
    const value = event.currentTarget.value

    if (name === 'id') {
      if (!value.length) {
        const newState = { ...filtersState }
        delete newState['id']

        setFiltersState(newState)
      } else {
        setFiltersState((prev) => ({
          ...prev,
          [name]: value,
        }))
      }
    } else if (name === 'type') {
      if (value === 'Choose type') {
        const newState = { ...filtersState }
        delete newState['type']

        setFiltersState(newState)
      } else {
        setFiltersState((prev) => ({
          ...prev,
          [name]: value,
        }))
      }
    }
  }

  const handleChangeCurrencies = (selected: Option[]) => {
    if (selected.length === 0) {
      const newState = { ...filtersState }
      delete newState['currency']

      setFiltersState(newState)
    } else {
      setFiltersState((prev) => ({
        ...prev,
        currency: selected.map((item) => item.value as string),
      }))
    }
  }

  const handleChangeGateways = (selected: Option[]) => {
    if (selected.length === 0) {
      const newState = { ...filtersState }
      delete newState['gateways']

      setFiltersState(newState)
    } else {
      setFiltersState((prev) => ({
        ...prev,
        gateways: selected.map((item) => item.value as string),
      }))
    }
  }

  const handleChangeTypes = (selected: Option[]) => {
    if (selected.length === 0) {
      const newState = { ...filtersState }
      delete newState['type']

      setFiltersState(newState)
    } else {
      setFiltersState((prev) => ({
        ...prev,
        type: selected.map((item) => item.value as string),
      }))
    }
  }

  const openFilterModal = () => {
    setIsFilterModalVisible(true)
  }

  const closeFilterModal = () => {
    setIsFilterModalVisible(false)
  }

  const handelCloseModal = (
    modalName: keyof Omit<TransactionModalState, 'transactionId'>,
  ) => {
    setIsOpenModal((prevState) => ({
      ...prevState,
      [modalName]: false,
      transactionId: '',
    }))
    setTimeout(() => {
      reloadTransactions()
    }, 1000)
  }

  const handelOpenModal = (
    modalName: keyof Omit<TransactionModalState, 'transactionId' | 'wallet'>,
    transaction: TransactionsDetailsItemFromServer,
  ) => {
    setIsOpenModal((prevState) => ({
      ...prevState,
      [modalName]: true,
      transaction,
    }))
  }

  const editTransactionPayload = useMemo<
    EditWalletTransaction | undefined
  >(() => {
    // Реальный id кошелька известен только на странице конкретного кошелька;
    // на странице "in progress" транзакции агрегированы по всем кошелькам,
    // поэтому там модалку редактирования не показываем
    if (walletStatus !== WalletStatus.general || !walletId) {
      return undefined
    }

    const rawEditTransaction = transactionsDetails.find(
      (item) => item.id === isOpenModal.transaction?.id,
    )

    if (!rawEditTransaction) {
      return undefined
    }

    return {
      transactionId: Number(rawEditTransaction.id),
      type: rawEditTransaction.type as EditWalletTransaction['type'],
      wallet: Number(walletId),
      amount: Number(rawEditTransaction.amount),
      currency: rawEditTransaction.currency,
      ourCommission: Number(rawEditTransaction.ourCommission),
      ourCommissionCurrency: rawEditTransaction.ourCommissionCurrency,
    }
  }, [transactionsDetails, isOpenModal.transaction, walletStatus, walletId])

  return (
    <Card style={{ width: '100%' }}>
      <Flex vertical gap={24}>
        <Flex justify="space-between">
          <Typography.Title level={3}>Transactions</Typography.Title>
          <CButton color="primary" variant="outline" onClick={openFilterModal}>
            <CIcon className="me-1" icon={cilDataTransferDown} />
            Export to CSV
          </CButton>
        </Flex>
        <div>
          <Flex justify="space-between">
            <Flex gap={5} style={{ height: 'auto', width: '80%' }}>
              <CFormInput
                style={{ width: '170px' }}
                placeholder="Search"
                id="id"
                name="id"
                size="sm"
                value={filtersState.id as string}
                onChange={handleChange}
              />
              <CMultiSelect
                style={{ width: '130px' }}
                virtualScroller
                placeholder="Type"
                size="sm"
                options={transactionDetailsType.map((item) => ({
                  text: item.label,
                  value: item.value,
                }))}
                onChange={handleChangeTypes}
              />
              {gatewaysDetail.length !== 0 && (
                <CMultiSelect
                  className="w-50"
                  style={{ width: '335px' }}
                  virtualScroller
                  placeholder="Gateway"
                  size="sm"
                  options={gatewaysDetail.map((item) => ({
                    text: `${item.gatewayId} - ${item.name}`,
                    value: item.gatewayId,
                  }))}
                  onChange={handleChangeGateways}
                />
              )}
              {currenciesFromWallet && (
                <CMultiSelect
                  className="w-50"
                  style={{ width: '335px' }}
                  virtualScroller
                  placeholder="Currency"
                  size="sm"
                  options={currenciesFromWallet.map((item) => ({
                    text: item,
                    value: item,
                  }))}
                  onChange={handleChangeCurrencies}
                />
              )}
              <DatePicker.RangePicker
                format="YYYY-MM-DD"
                style={{ width: '270px' }}
                value={filtersState.period as any}
                onChange={(e) => {
                  const result = e ? [e] : []

                  setFiltersState((prevState) => ({
                    ...prevState,
                    period: result[0],
                  }))
                }}
                presets={rangePresets}
                allowEmpty={[true, true]}
              />
            </Flex>
          </Flex>
          <CSmartTable
            columnSorter
            columns={tableColumns}
            tableProps={{
              striped: true,
              responsive: true,
            }}
            itemsPerPage={Math.max(tableItems.length, TRANSACTIONS_PAGE_SIZE)}
            pagination={false}
            items={tableItems}
            // @ts-ignore
            onSorterChange={(sorter) => setColumnSorter(sorter)}
            scopedColumns={mapScopedColumns(handelOpenModal)}
            loading={transactionsLoading && requestedPage === 1}
          />
          <div
            ref={sentinelRef}
            style={{ display: 'flex', justifyContent: 'center', padding: 16 }}
          >
            {isLoadingMore && <CSpinner size="sm" />}
          </div>
        </div>
        {isOpenModal.createOtherModal && (
          <OtherTransactionModal
            mode="edit"
            isOpen={isOpenModal.createOtherModal}
            transactionId={isOpenModal.transaction?.id}
            onClose={() => handelCloseModal('createOtherModal')}
            type="wallet"
          />
        )}
        {isOpenModal.createBudgetPaymentModal && (
          <BudgetPaymentTransactionModal
            mode="edit"
            isOpen={isOpenModal.createBudgetPaymentModal}
            transactionId={isOpenModal.transaction?.id}
            onClose={() => handelCloseModal('createBudgetPaymentModal')}
            type="wallet"
          />
        )}
        {isOpenModal.createDividendModal && (
          <DividendTransactionModal
            mode="edit"
            allCurrencies={allCurrencies}
            transactionId={isOpenModal.transaction?.id}
            transactionType={isOpenModal.transaction?.type}
            isOpen={isOpenModal.createDividendModal}
            onClose={() => handelCloseModal('createDividendModal')}
            type="wallet"
          />
        )}
        {isOpenModal.createInvestmentModal && (
          <InvestmentTransactionModal
            mode="edit"
            allCurrencies={allCurrencies}
            transactionId={isOpenModal.transaction?.id}
            transactionType={isOpenModal.transaction?.type}
            isOpen={isOpenModal.createInvestmentModal}
            onClose={() => handelCloseModal('createInvestmentModal')}
            type="wallet"
          />
        )}
        {isOpenModal.createTransferModal && (
          <TransferTransactionModal
            mode="edit"
            transactionId={isOpenModal.transaction?.id}
            isOpen={isOpenModal.createTransferModal}
            onClose={() => handelCloseModal('createTransferModal')}
            type="wallet"
          />
        )}
        {isOpenModal.createCommissionPaymentModal && (
          <CommissionPaymentTransactionModal
            mode="edit"
            transactionId={isOpenModal.transaction?.id}
            isOpen={isOpenModal.createCommissionPaymentModal}
            onClose={() => handelCloseModal('createCommissionPaymentModal')}
            type="wallet"
          />
        )}
        {isOpenModal.editWalletTransactionModal && editTransactionPayload && (
          <EditWalletTransactionModal
            transaction={editTransactionPayload}
            allCurrencies={allCurrencies}
            isModalOpen={isOpenModal.editWalletTransactionModal}
            onClose={() => handelCloseModal('editWalletTransactionModal')}
          />
        )}
        <FilterModal
          isOpen={isFilterModalVisible}
          onClose={() => setIsFilterModalVisible(false)}
        />
      </Flex>
    </Card>
  )
}
